import { useState, useEffect, useCallback } from 'react';
import BookingPlansAPI, { BookingPlan } from '../api/BookingPlansAPI';
import { toastService } from '../services/ToastService';
import { useTranslation } from 'react-i18next';

type UseBookingPlansReturn = {
  bookingPlans: BookingPlan[];
  isLoading: boolean;
  getBookingPlans: () => Promise<void>;
  getBookingPlanById: (id: number) => Promise<BookingPlan | null>;
  createBookingPlan: (bookingPlan: BookingPlan) => Promise<BookingPlan | null>;
  updateBookingPlan: (id: number, bookingPlan: BookingPlan) => Promise<BookingPlan | null>;
  deleteBookingPlan: (id: number) => Promise<boolean>;
  setBookingLink: (alias: string) => Promise<string | null>;
};

export function useBookingPlans(): UseBookingPlansReturn {
  const { t } = useTranslation();
  const [bookingPlans, setBookingPlans] = useState<BookingPlan[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getBookingPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await BookingPlansAPI.getAll();
      setBookingPlans(response.data);
    } catch (err) {
      toastService.showToast(t('toast.errors.booking_plans.load'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const getBookingPlanById = useCallback(
    async (id: number): Promise<BookingPlan | null> => {
      setIsLoading(true);
      try {
        const response = await BookingPlansAPI.getById(id);
        return response.data;
      } catch (err) {
        toastService.showToast(t('toast.errors.booking_plans.load'), 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const createBookingPlan = useCallback(
    async (bookingPlan: BookingPlan): Promise<BookingPlan | null> => {
      setIsLoading(true);
      try {
        const response = await BookingPlansAPI.create(bookingPlan);
        setBookingPlans((prev) => [...prev, response.data]);
        toastService.showToast(t('toast.success.booking_plans.create'), 'success');
        return response.data;
      } catch (err) {
        toastService.showToast(t('toast.errors.booking_plans.create'), 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const updateBookingPlan = useCallback(
    async (id: number, bookingPlan: BookingPlan): Promise<BookingPlan | null> => {
      setIsLoading(true);
      try {
        const response = await BookingPlansAPI.update(id, bookingPlan);
        setBookingPlans((prev) =>
          prev.map((bp) => (bp.id === id ? response.data : bp))
        );
        toastService.showToast(t('toast.success.booking_plans.update'), 'success');
        return response.data;
      } catch (err) {
        toastService.showToast(t('toast.errors.booking_plans.update'), 'error');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const deleteBookingPlan = useCallback(
    async (id: number): Promise<boolean> => {
      setIsLoading(true);
      try {
        await BookingPlansAPI.delete(id);
        setBookingPlans((prev) => prev.filter((bp) => bp.id !== id));
        toastService.showToast(t('toast.success.booking_plans.delete'), 'success');
        return true;
      } catch (err) {
        toastService.showToast(t('toast.errors.booking_plans.delete'), 'error');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const setBookingLink = useCallback(
    async (alias: string): Promise<string | null> => {
      setIsLoading(true);
      try {
        const response = await BookingPlansAPI.setBookingLink(alias);
        return response.data;
      } catch (err) {
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    getBookingPlans();
  }, [getBookingPlans]);

  return {
    bookingPlans,
    isLoading,
    getBookingPlans,
    getBookingPlanById,
    createBookingPlan,
    updateBookingPlan,
    deleteBookingPlan,
    setBookingLink
  };
}